import { AplosFileLineage } from "../models/AplosFileLineage";
import { AplosFile } from "../models/AplosFile";
import { HierarchicAplosFileViewModel } from "../models/HierarchicAplosFileViewModel";
import { AplosNcaInputStatus } from "../models/AplosNcaInputStatus";
import { AplosFileState } from "../models/AplosFileState";

export class HierarchicAplosFileService {

    public static buildHierarchicArray(flatList: Array<AplosFile>): Array<HierarchicAplosFileViewModel> {

        // Return early for empty input
        if (!Array.isArray(flatList) || flatList.length === 0) return [];

        // Work on shallow clones so we don't mutate caller-owned objects (internalFiles etc.)
        flatList = flatList.map(item => ({ ...item }));

        // Build id -> item map (do NOT pre-initialize children)
        const idMap = new Map<string, HierarchicAplosFileViewModel>();
        flatList.forEach(item => {
            if (item.id) idMap.set(item.id, item);
        });

        // Identify original roots: id exists, rootId === id, and parentId is null/undefined
        const originalRoots: HierarchicAplosFileViewModel[] = flatList.filter(item =>
            !!item.id && item.rootId === item.id && (item.parentId === null || item.parentId === undefined)
        );

        // Build final roots array, promoting children when parentId === rootId === root.id (and child's id !== root.id)
        const finalRoots: HierarchicAplosFileViewModel[] = [];
        const replacedRootIds = new Set<string>();

        // Find promote child if root is not "analysis-input" E.g. "Excel" and needed converion
        originalRoots.forEach(root => {
            // Build list of promotable items (should be non-cleaned or converted files)
            const nonCleanedPromoted = flatList.filter(it =>
                it.parentId === root.id
                && it.rootId === root.id
                && it.id !== root.id
                && it?.lineage !== AplosFileLineage.Cleaned) as Array<HierarchicAplosFileViewModel>;

            // Only allow promotion on root items that are converted originals E.g. Excel => CSV or ncaInput "approved" originals
            const isConversionRequiredRoot = root.metadata?.ncaInput === AplosNcaInputStatus.ConversionRequired && root.lineage === AplosFileLineage.Original;
            const isOriginalApprovedNcaInputRoot = root.metadata?.ncaInput === AplosNcaInputStatus.Approved && root.lineage === AplosFileLineage.Original;
            if ((isConversionRequiredRoot || isOriginalApprovedNcaInputRoot) && nonCleanedPromoted.length > 0) {
                // Mark this original root as replaced and don't include it in finalRoots
                replacedRootIds.add(root.id as string);
                nonCleanedPromoted.forEach(p => {
                    // set reference to the original root it replaces
                    p.rootItemForPromoted = root;
                    finalRoots.push(p);
                });
            } else {
                finalRoots.push(root);
            }
        });

        // Helper: find a final root corresponding to a rootId (direct match or matching rootItem)
        const findFinalRootForRootId = (rootId?: string) => {
            if (!rootId) return undefined;
            return finalRoots.find(fr => fr.id === rootId || (fr.rootItemForPromoted && fr.rootItemForPromoted.id === rootId));
        };

        // Track attached items to avoid duplicates
        const attachedIds = new Set<string>();

        // Attach each non-root item to an allowed parent (final root). Limit tiers to 2.
        flatList.forEach(item => {
            // Skip items that are represented as final roots (either original root or promoted)
            if (finalRoots.includes(item)) return;

            // Skip original roots that were replaced (they should not appear as children)
            if (item.id && replacedRootIds.has(item.id)) return;

            // If already attached (by id) skip
            if (item.id && attachedIds.has(item.id)) return;

            let attached = false;

            // Try to attach to direct parent if present
            if (item.parentId) {
                // Don't consider direct parent if it's a replaced original root
                if (replacedRootIds.has(item.parentId)) {
                    // treat as if direct parent is not valid (will fallback to final root)
                } else {
                    const directParent = idMap.get(item.parentId);
                    // If directParent exists and is a final root, attach directly (this is second tier)
                    if (directParent && finalRoots.includes(directParent)) {
                        directParent.children = directParent.children || [];
                        // avoid duplicates in children
                        if (!directParent.children.some((c: HierarchicAplosFileViewModel) => c.id === item.id)) {
                            directParent.children.push(item);
                        }
                        attached = true;
                    } else if (directParent) {
                        // directParent exists but is NOT a final root (would create >2 tiers).
                        // Find the appropriate final root for this item's rootId and attach there instead.
                        const targetFinal = findFinalRootForRootId(item.rootId);
                        if (targetFinal) {
                            targetFinal.children = targetFinal.children || [];
                            if (!targetFinal.children.some((c: HierarchicAplosFileViewModel) => c.id === item.id)) {
                                targetFinal.children.push(item);
                            }
                            attached = true;
                        } else {
                            // No matching final root; as a fallback attach to the top-most root node referenced by rootId if present
                            const fallbackRootNode = idMap.get(item.rootId || "");
                            // don't attach to replaced original root as a fallback
                            if (fallbackRootNode && !(fallbackRootNode.id && replacedRootIds.has(fallbackRootNode.id))) {
                                fallbackRootNode.children = fallbackRootNode.children || [];
                                if (!fallbackRootNode.children.some((c: HierarchicAplosFileViewModel) => c.id === item.id)) {
                                    fallbackRootNode.children.push(item);
                                }
                                attached = true;
                            }
                        }
                    }
                }
            }

            // If still not attached, try to attach to the final root that matches item.rootId
            if (!attached && item.rootId) {
                // Prefer promoted/final roots; fallback to idMap entry only if that entry isn't a replaced original
                const rootNode = findFinalRootForRootId(item.rootId) || idMap.get(item.rootId);
                if (rootNode && !(rootNode.id && replacedRootIds.has(rootNode.id))) {
                    rootNode.children = rootNode.children || [];
                    if (!rootNode.children.some((c: HierarchicAplosFileViewModel) => c.id === item.id)) {
                        rootNode.children.push(item);
                    }
                    attached = true;
                }
            }

            // Else add as a root-level element even if not a root or promoted
            if (!attached) {
                finalRoots.push(item);
                attached = true;
            }

            // mark attached (if it has an id)
            if (item.id && attached) attachedIds.add(item.id);
        });

        // Cleanup: replace empty children arrays with undefined and deduplicate children arrays by id
        idMap.forEach(node => {
            if (Array.isArray(node.children)) {
                // dedupe by id preserving order
                const seen = new Set<string>();
                node.children = node.children.filter((child: HierarchicAplosFileViewModel) => {
                    if (!child.id) return true; // keep if no id
                    if (seen.has(child.id)) return false;
                    seen.add(child.id);
                    return true;
                });
                if (node.children.length === 0) {
                    node.children = undefined;
                }
            }
        });

        // Populate operableAnalysisFileId:
        // - For each final root, set operableAnalysisFileId = root.id (promoted items get their own id)
        // - For each direct child of a final root, set operableAnalysisFileId = parent.id
        finalRoots.forEach(root => {
            if (root && root.id) {
                root.operableAplosFileId = root.id;
            }
            if (Array.isArray(root.children)) {
                root.children.forEach((child: HierarchicAplosFileViewModel) => {
                    if (root && root.id) {
                        // ensure child references the final root (promoted id) as its operable id
                        child.operableAplosFileId = root.id;
                    }
                });
            }
        });

        // Return final root set (promoted items replace original roots where applicable)
        return this.orderHeirarchicArrayByUploadedDateDescending(finalRoots);
    }

    private static orderHeirarchicArrayByUploadedDateDescending(items: Array<HierarchicAplosFileViewModel>): Array<HierarchicAplosFileViewModel> {
        // Guard: empty input
        if (!Array.isArray(items) || items.length === 0) return [];

        // Helper: parse uploadedUtc string to timestamp (descending order). Treat invalid/missing as 0.
        const ts = (s?: string): number => {
            if (!s) return 0;
            const t = Date.parse(s);
            return isNaN(t) ? 0 : t;
        };

        // Work on shallow clones to avoid mutating caller-owned objects.
        const clonedRoots = items.map(root => {
            const cloned: HierarchicAplosFileViewModel = { ...root };
            if (Array.isArray(root.children)) {
                cloned.children = root.children.map(c => ({ ...c }));
                // Order children descending by uploadedUtc
                cloned.children.sort((a: HierarchicAplosFileViewModel, b: HierarchicAplosFileViewModel) =>
                    ts(b.createdUtc as unknown as string) - ts(a.createdUtc as unknown as string)
                );
            }
            return cloned;
        });

        // Order top-level roots descending by uploadedUtc
        clonedRoots.sort((a, b) => ts(b.createdUtc as unknown as string) - ts(a.createdUtc as unknown as string));

        return clonedRoots;
    }

    // New: build hierarchic array from a filtered subset plus a source/unfiltered list
    public static buildHierarchicArrayFromFiltered(
        filteredList: Array<AplosFile>,
        sourceList: Array<AplosFile>
    ): Array<HierarchicAplosFileViewModel> {
        // Guard
        if (!Array.isArray(filteredList) || filteredList.length === 0) return [];

        // Build source id -> item map (shallow clones to avoid mutating original)
        const sourceMap = new Map<string, HierarchicAplosFileViewModel>();
        sourceList.forEach(item => {
            if (item.id) sourceMap.set(item.id, { ...item });
        });

        // Preserve original filtered ids so we only mark those as matched (ancestry should not be marked)
        const filteredIdSet = new Set<string>();
        filteredList.forEach(item => {
            if (item.id) filteredIdSet.add(item.id);
        });

        // Start with ids present in filteredList
        const includeIds = new Set<string>();
        filteredList.forEach(item => {
            if (item.id) includeIds.add(item.id);
        });

        // For each filtered item, include ancestry (parent chain) and rootId from the source map
        filteredList.forEach(item => {
            // include explicit rootId if present
            if (item.rootId) {
                includeIds.add(item.rootId);
            }

            // walk parent chain from this item using source map (stop if parent not found)
            let curParentId = item.parentId;
            while (curParentId) {
                if (includeIds.has(curParentId)) break;
                const parent = sourceMap.get(curParentId);
                if (!parent) break;
                includeIds.add(curParentId);

                // ensure parent's root is included
                if (parent.rootId) includeIds.add(parent.rootId);

                curParentId = parent.parentId;
            }
        });

        // Build a combined list in the same order as sourceList (keeps predictable ordering)
        const combined: HierarchicAplosFileViewModel[] = [];
        sourceList.forEach(item => {
            if (item.id && includeIds.has(item.id)) {
                // use the cloned sourceMap item to avoid mutation of original
                const cloned = sourceMap.get(item.id);
                if (cloned) {
                    // mark as matched only if this id was present in the original filteredList
                    if (filteredIdSet.has(item.id)) cloned.hasMatchedSearch = true;
                    combined.push(cloned);
                }
            }
        });

        // Also ensure any filtered items not present in source (no id or missing) are included
        filteredList.forEach(item => {
            if (!item.id || !sourceMap.has(item.id)) {
                // mark these as matched since they originated from the filteredList
                combined.push({ ...item, hasMatchedSearch: true });
            }
        });

        // Delegate to existing builder to construct the final hierarchy
        return this.buildHierarchicArray(combined);
    }

    public static findRootForFile(file: HierarchicAplosFileViewModel, sourceFiles: Array<HierarchicAplosFileViewModel>): HierarchicAplosFileViewModel {
        if (!file) return file

        // Prefer operableAplosFileId if available
        if (file.operableAplosFileId) {
            const byOperable = sourceFiles.find((r) => r.id === file.operableAplosFileId)
            if (byOperable) return byOperable
        }

        // Prefer rootItem if it maps to a top-level displayed root
        if (file.rootItemForPromoted?.id) {
            const byRootItem = sourceFiles.find((r) => r.id === file.rootItemForPromoted?.id)
            if (byRootItem) return byRootItem
        }

        // Traverse top-level displayFiles to find the root that contains this record
        const containsId = (node: HierarchicAplosFileViewModel | undefined, id?: string): boolean => {
            if (!node || !id) return false
            if (!Array.isArray(node.children)) return false
            for (const child of node.children) {
                if (child.id === id) return true
                if (containsId(child, id)) return true
            }
            return false
        }

        for (const root of sourceFiles) {
            if (root.id === file.id) return root
            if (containsId(root, file.id)) return root
        }

        // Fallback to the record itself when no top-level root is found
        return file
    }

    public static getAllParentsForFile(file: HierarchicAplosFileViewModel, sourceFiles: Array<HierarchicAplosFileViewModel>): Array<HierarchicAplosFileViewModel> {
        if (!file) return [];

        // Build id -> item map for quick parent lookups
        const idMap = new Map<string, HierarchicAplosFileViewModel>();
        if (Array.isArray(sourceFiles)) {
            sourceFiles.forEach(f => { if (f?.id) idMap.set(f.id, f); });
        }

        // Helper to determine root per existing build logic
        const isRootNode = (n?: HierarchicAplosFileViewModel) =>
            !!n?.id && n.rootId === n.id && (n.parentId === null || n.parentId === undefined);

        // Scenario 1: file is a root
        if (isRootNode(file)) return [file];

        // Scenario 2: file is a promoted item
        if (file.rootItemForPromoted) {
            return [file, file.rootItemForPromoted];
        }

        // Scenario 3: walk parent chain
        const result: HierarchicAplosFileViewModel[] = [];
        const seenIds = new Set<string>();
        const seenObjs = new Set<HierarchicAplosFileViewModel>();

        const pushUnique = (n?: HierarchicAplosFileViewModel) => {
            if (!n) return;
            if (n.id) {
                if (seenIds.has(n.id)) return;
                seenIds.add(n.id);
            } else {
                if (seenObjs.has(n)) return;
                seenObjs.add(n);
            }
            result.push(n);
        };

        // include the file itself first
        pushUnique(file);

        let curParentId = file.parentId;
        while (curParentId) {
            // avoid infinite loops
            if (seenIds.has(curParentId)) break;

            const parent = idMap.get(curParentId);
            if (!parent) break;

            pushUnique(parent);

            // if parent is promoted, include its rootItemForPromoted and stop
            if (parent.rootItemForPromoted) {
                pushUnique(parent.rootItemForPromoted);
                break;
            }

            // if parent is a top-level root, stop
            if (isRootNode(parent)) break;

            // continue up the chain
            curParentId = parent.parentId;
        }

        return result;
    }

    public static getFileDisplayNameForFile(file: HierarchicAplosFileViewModel): string {
        const fileName = file?.name || '';
        const originalFileName =
            file.rootItemForPromoted != null ? file.rootItemForPromoted.name ?? null : null;

        return `${fileName} ${originalFileName ? '(' + originalFileName + ')' : ''}`;
    }


    public static getFileErrorsForManager = (file: HierarchicAplosFileViewModel): Array<string> => {
        let errorState: Array<string> = [];

        if (file?.state !== AplosFileState.Ready) errorState.push('File is not ready.');

        const errors = file?.errors;
        if (errors && Object.keys(errors).length) {
            Object.entries(errors).forEach(([key, value]) => {
                errorState.push(`${key}: ${value}`);
            });
        }

        return errorState;
    }

}